import React, { useCallback, useEffect, useRef } from 'react';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { ForumPost } from '../../../components';
import type { FormForumCommentValues } from '../../../components/Forms/Forum/Comment';
import type { ForumPostProps } from '../../../components/Forum/Post';
import { FORUM_COMMENT_ID_PREFIX } from '../../../config/constants';
import useToast from '../../../hooks/useToast';
import useTypedDispatch from '../../../hooks/useTypedDispatch';
import { commentEdit } from '../../../store/actions/Entity/Comment';
import type { ImmutableForumCommentEntity } from '../index';

export interface CommentForumProps {
  entity: ImmutableForumCommentEntity;
  isOriginalPoster: boolean;
  onClickReply: ForumPostProps['onClickReply'];
}

const CommentForum: React.FC<CommentForumProps> = (props) => {
  const {
    entity,
    isOriginalPoster,
    onClickReply,
  } = props;

  const commentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const dispatch = useTypedDispatch();
  const toast = useToast();

  const currentUserId = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], null));

  const nid = entity.getIn(['entity_id', 0, 'target_id'], null);
  const cid = entity.getIn(['cid', 0, 'value'], null);
  const created = entity.getIn(['created', 0, 'value']);
  const changed = entity.getIn(['changed', 0, 'value']);
  const avatar = entity.getIn(['uid', 0, 'entity', 'field_avatar', 0, 'value'], 1) as number;
  const authorId = entity.getIn(['uid', 0, 'target_id']);
  const authorName = entity.getIn(['uid', 0, 'entity', 'name', 0, 'value'], '') as string;
  const message = entity.getIn(['field_body', 0, 'value'], '');
  const messageProcessed = entity.getIn(['field_body', 0, 'processed'], '');
  const likeCount = entity.getIn(['flags', 'like'], Immutable.List()).size;
  const parentCommentId = entity.getIn(['pid', 0, 'target_id'], null);
  const parentCommentLink = entity.getIn(['parent_comment', 0, 'entity', 'permalink']);
  const parentAuthorName = entity.getIn(['parent_author', 0, 'entity', 'name', 0, 'value'], '') as string;
  const nickname = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['user', currentUserId, 'field_nickname', 0, 'value'], ''));
  const hash = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['router', 'location', 'hash'], ''));

  const isLiked = entity.getIn(['flags', 'like'], Immutable.List()).reduce((acc, flag) => {
    if (!acc) {
      return flag.getIn(['uid', 0, 'target_id']) === currentUserId;
    }

    return acc;
  }, false);

  const isInappropriate = entity.getIn(['flags', 'inappropriate'], Immutable.List()).reduce((acc, flag) => {
    if (!acc) {
      return flag.getIn(['uid', 0, 'target_id']) === currentUserId;
    }

    return acc;
  }, false);

  const handleEditComment = useCallback(async (values: FormForumCommentValues) => {
    const comment = {
      entity_id: [{ target_id: nid }],
      entity_type: [{ value: 'node' }],
      comment_type: [{ target_id: 'comment_forum' }],
      field_name: [{ value: 'comment_forum' }],
      field_body: [{ value: values.body }],
    };

    try {
      // Wait for the comment to be added.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      await dispatch(commentEdit(cid, comment));

      // Show success toast.
      toast.show(t('pages.forum_comment_edit.success_message'), 'success');
    } catch (error) {
      if (error) {
        toast.show(t('pages.forum_comment_edit.error_message'), 'error');
      }
    }
  }, [cid, dispatch, nid, t, toast]);

  // Focus and scroll to mentioned comment
  // if it matches the hash from the url.
  useEffect(() => {
    if (!commentRef.current) {
      return;
    }

    if (!hash || !hash.startsWith(`#${FORUM_COMMENT_ID_PREFIX}`)) {
      return;
    }

    const id = hash.slice(`#${FORUM_COMMENT_ID_PREFIX}`.length);

    if (id !== String(cid)) {
      return;
    }

    const element = commentRef.current;
    element.focus();
    element.scrollIntoView();
  // We only want to check for this on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ForumPost
      ref={commentRef}
      id={cid}
      type="comment"
      author={authorName}
      avatar={avatar}
      publicationDate={new Date(created)}
      updatedDate={new Date(changed)}
      message={message}
      messageProcessed={messageProcessed}
      mentionId={parentCommentId}
      mentionName={parentAuthorName}
      mentionLink={parentCommentLink}
      isLiked={isLiked}
      likeCount={likeCount}
      isInappropriate={isInappropriate}
      isMyPost={currentUserId === authorId}
      isOriginalPoster={isOriginalPoster}
      hasCreatedNickname={!!nickname}
      isFirstPost={false}
      onClickReply={onClickReply}
      onEditPost={handleEditComment}
    />
  );
};

export default CommentForum;
